import React, {FC, useEffect, useState} from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Theme,
  Typography, useMediaQuery
} from "@material-ui/core";
import {fetchLogsByObjectId} from "../../../../../../../services/logsEntryService";
import {useParams} from "react-router-dom";
import {useSelector} from "../../../../../../../redux/store";
import {LogEntry, LogEntryQuery} from "../../../../../../../models/LogEntry";
import styled from "styled-components/macro";
import {fetchUser} from "../../../../../../../services/userService";
import {AxiosResponse} from "axios";
import {IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import 'moment/locale/sv'
import 'moment/locale/da'
import HistoryTable from "./components/HistoryTable";
import PaginationBlock from "./components/PaginationBlock";

const TopBar = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: relative;
    .MuiButtonBase-root {
        width: 42px;
        color: rgba(18, 22, 25, 0.5);
    }
`

const NoHaveData = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 400px;
`

// user caching for optimization
const cachedUsers: any = [];

type PropsType = {
  open: boolean;
  title: string;
  onClose: () => void
}

const HistoryDialog: FC<PropsType> = ({open, title, onClose}) => {
  const ELEMENT_LIMIT = 5
  const { t } = useTranslation()
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const params: {id: string} = useParams()
  const user = useSelector((state) => state.authReducer.user);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0)
  const [logEntry, setLogEntry] = useState<LogEntry[]>([])

  const getUserById = async (id: string, token: string): Promise<string> => {
    let response: any = null
    try {
      response = await fetchUser(id, token)
    } catch (e) {
      console.error('User not found')
    }
    return response?.data
  }

  const prepareResults = async (response: AxiosResponse<LogEntryQuery>) => {
    let results = response.data.results
    if (results.length > 0) {
      for await (let item of results) {
        if (item.user) {
          const cachedUser = cachedUsers.find((u: any) => u?.id === item?.user)
          if (cachedUser) {
            item.user = cachedUser
          } else {
            const newUser = await getUserById(item.user, user!.token)
            cachedUsers.push(newUser)
            item.user = newUser
          }
        }
      }
    }
    return results
  }


  const handleChangePage = (_: any, value: number) => {
    fetchLogsByObjectId(user!.token, params.id, value, ELEMENT_LIMIT).then(async (response) => {
      setPage(value)
      const results = await prepareResults(response)
      setLogEntry(results)
    })
  };

  useEffect(() => {
    if (logEntry.length === 0) {
      fetchLogsByObjectId(user!.token, params.id, page, ELEMENT_LIMIT).then(async (response) => {
        if (count === 0) {
          setCount(response.data.count)
        }
        const results = await prepareResults(response)
        setLogEntry(results)
      })
    }
  }, [])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth={'lg'}
      PaperProps={{
        style: {
          borderRadius: '8px',
          padding: sm ? 0 : '10px 20px',
          margin: sm ? 0 : '32px',
          width: sm ? 'unset' : 'calc(100% - 64px)'
        },
      }}
    >
      <TopBar>
        <DialogTitle disableTypography>
          <Typography variant="h4" align="center">{title}</Typography>
        </DialogTitle>
        <IconButton
          style={{position: 'absolute', top: '10px', right: '10px'}}
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon/>
        </IconButton>
      </TopBar>

      <DialogContent>
        {logEntry.length > 0 ? (
          <Box>
            <Box style={{overflow: 'auto', maxHeight: '425px'}}>
              <HistoryTable data={logEntry}/>
            </Box>
            <Box>
              {count > ELEMENT_LIMIT ? (
                <PaginationBlock
                  page={page}
                  count={count}
                  limit={ELEMENT_LIMIT}
                  onChangePage={handleChangePage}
                />
              ) : null}
            </Box>
          </Box>
        ) : (
          <NoHaveData>
            <Typography style={{color: '#808080'}} variant='h4'>
              {t('statsPage_noDataAvailable_topText')}
            </Typography>
          </NoHaveData>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default HistoryDialog